/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * Created by cmiller on 8/19/14.
 */

// import {Dyn} from '../dynDataSet/dynMiscellany';
import Raphael from "raphael";

export const wrapDynIconStyler = function( Dyn ) {

    // values for Dyn.IconStyler.style
    Dyn.EIconStyle = {
        kNoStyle: 0,	// no style is set yet
        kCircle: 1,	// circle with fixed .radius + casePosition.x, .y
        kCirclePath: 2,	// circular path (so we can animate from one path to another)
        kRect: 3,	// rectangle with .height and .width + casePosition.x, .y
        kRectPath: 4,	// rectangular path (so we can animate from one path to another)
        kSlice: 5,	// pie slice: .radius, .arcStart, .arcLength + casePosition.x, .y
        kCustomIcon: 6,    // customized case icon for the particular dataset, used where kCircle icon is used
        kVariableIcon: 7     // variable icon, one per category/attribute, used where kCircle icon is used.
    };

    class IconStyler {
        /**
         * Dyn.IconStyler -- stores the case icon style, common to all icons in a plot,
         * which could be a circle, bar, rectangle, pie slice, etc.
         * Includes methods to construct Rafael icon paths, and transform from one icon style to another.
         * @constructor
         */
        constructor() {
            this.style = Dyn.EIconStyle.kCircle;
            this.radius = 1;	// updated by calcCaseIconRadius()
            this.hideBorder = false;
            this.missingValuePosition = { x: 0, y: 0 };
            this.iconSize = 0;    // user-specified case icon size (Circle or Custom Case Icon), 0==standard, 2+ == user-specified
            //this.height
            //this.width
            //this.customCaseIcon = null;
        }

        get allowVariableIconStyle() { return true; } // set to true to allow this style for multi-attribute plots

        get kCaseIconRadiusMax() { return 10; }
        get kCaseIconRadiusMin() { return 4; }
        get kCaseIconRadiusBase() { return 3.0; } // <=3 cases gives a radius 10, <=9 cases a radius 9, <=18 cases radius 7, ... radius 4

        get kFirstSliceStartAngle() { return Dyn.mathUtil.kTwoPi * 0.75; } // slice starting angle in radians (noon rather than 3 o'clock)
        get kLabelRadiusFraction() { return 0.60; } // labels are centered 60/100 of distance from center to edge of circle

        save() {
            // save values only if not the default, so that defaults can be overriden in later versions
            var iconData = {};
            if( this.iconSize !== 0 )
                iconData.size = this.iconSize;
            if( this.customCaseIcon ) {
                iconData.pathString = this.customCaseIcon.pathString;
                iconData.isEnabled = this.customCaseIcon.isEnabled;
            }

            return iconData;
        }

        restore( iconData, paper ) {
            if( iconData ) {
                if( isFinite( iconData.size ) )
                    this.iconSize = (iconData.size > 0) ? iconData.size : 0;
                if( iconData.pathString && paper ) {
                    this.setCustomCaseIcon( paper, iconData.pathString, iconData.isEnabled );
                }
            }
        }

        /**
         * Store the custom case icon, or clear it pathString is undefined
         * See setStyleCustomIcon() and setStyleCircleOrCustomIcon() for use.
         * @param paper
         * @param pathString
         * @returns {boolean} -- true if custom icon is valid
         */
        setCustomCaseIcon( paper, pathString, wantEnabled = true ) {
            // Dyn.plotUtil.unitTest_validateSVG_PathString();  // for testing
            /* Note: any valid path can be used, even if it is not a closed path (ending in 'z').
               A non-closed path will be missing the outline between the first and last point.
             */
            if( paper && Dyn.plotUtil.isValidPathStringForSVG( pathString ) ) {
                this.customCaseIcon = {
                    pathString: pathString,
                    bbox: Dyn.plotUtil.getPathBBox( paper, pathString ),
                    isEnabled: Boolean( wantEnabled )
                }
                Dyn.assert( this.customCaseIcon && typeof this.customCaseIcon.pathString === "string" && this.customCaseIcon.bbox );
            } else {
                console.log( "setCustomCaseIcon() with invalid path string: ", pathString );
                this.customCaseIcon = null;
            }
            return this.hasCustomCaseIcon();
        }

        hasCustomCaseIcon() {
            return Boolean( this.customCaseIcon );
        }

        isCustomCaseIconEnabled() {
            return Boolean( this.customCaseIcon && this.customCaseIcon.isEnabled );
        }

        setCustomCaseIconEnabled( wantEnabled = true ) {
            if( this.customCaseIcon ) {
                this.customCaseIcon.isEnabled = Boolean( wantEnabled );
            }
        }

        /**
         * Set the icon size for Dot Plots and the like, or use the default size
         * if size===0.  See clipIconSize for allowable absolute icon sizes.
         * @param size
         * @returns {boolean} -- true if set successfully
         */
        setIconSize( size ) {
            let oldSize = this.iconSize,
                isInteger = Dyn.mathUtil.isNumber( size ) && Dyn.mathUtil.isInteger( size ),
                clippedInteger = (size === 0) ? 0 : this.clipIconSize( Math.abs( size ) ), // 0 == use plot defaults
                success = false;
            if( isInteger && size === clippedInteger ) {
                this.iconSize = clippedInteger;
                // NOTE: plot view responds to new dot size by refreshing plot, and
                // if Circle or Rectangle style icons, setStyleCircle() or setStyleRectangle()
                // will respond to new icon size, setStyleSlice() will ignore it.
                Dyn.mediator.publish( "pref:newDotSize", { iconSize: this.iconSize } );
                success = (this.iconSize !== oldSize);
            }
            //console.log("setIconSize", this.iconSize, "radius", this.radius);
            return success;
        }

        getIconSize() {
            return this.iconSize;
        }

        clipIconSize( s ) {
            return Dyn.mathUtil.clipToMinMax( 2, 200, s ); // 200 is arbitrary, 2 is min to see fill color
        }

        getWidthOrHeight( wantHorizontal ) {
            // placeholder for when we have multiple case icon types
            if( this.style === Dyn.EIconStyle.kRect ) {
                return (wantHorizontal ? this.width : this.height);
            } else {
                return (2 * this.radius);
            }
        }

        /**
         * Get the border color (Rafael: 'stroke' attribute) of a case icon, which varies by type.
         * For hidden border styles (e.g. kSlice, kRect), the border is same color as fill,
         * to blend in with neighbors of same color, unless there is not enough contrast with
         * the plot background color.
         * @param isSelected {boolean}
         * @param fillColorString {string}
         * @param [fillColorB255] {number} -- optional brightness of fill from calcColorBrightness()
         * @returns {*|string}
         */
        getBorderColor( isSelected, fillColorString, fillColorB255 ) {
            if( this.hideBorder ) {
                let checkContrast = Number.isFinite( fillColorB255 ),
                    contrast = checkContrast && Math.abs( fillColorB255 - Dyn.plotUtil.caseBackgroundBrightness );
                if( (!checkContrast) || (contrast >= Dyn.plotUtil.caseBackgroundMinContrast) ) {
                    return fillColorString // enough contrast; hide border for this case by using the fill color
                } else {
                    return Dyn.plotUtil.kCaseSubtleBorder; // not enough contrast; show subtle border
                }
            } else {
                // use bold border for non-hidden border style
                return Dyn.plotUtil.kCaseBorderColor;
            }
        }

        /**
         * Get the border width (Rafael: 'stroke-width' attribute) of a case icon, which varies by selection.
         * @param isSelected
         * @returns {number}
         */
        getBorderWidth( isSelected ) {
            return (isSelected ? 1 /*Dyn.plotUtil.kCaseIconBorderWidthSelected */ : 1);
        }

        getOutlineColor( caseColor ) {
            return Dyn.plotUtil.kCaseBorderColor;
        }

        getOutlineWidth( iconStyle ) {
            return 3;
        }

        getRadius( position, isSelected ) {
            // if selected, circles must be smaller but non-negative, to fit correctly inside selection outline.
            return this._adjustSizeIfSelected( position.r, isSelected, 0 );
        }

        getOutlineCircle( position ) {
            return {
                'x': position.x,
                'y': position.y,
                'r': position.r + 1
            };
        }

        getOutlineRect( position ) {
            return {
                'x': position.x - 1,//( isSelected ? -1 : 0 ),
                'y': position.y - 1, //( isSelected ? -1 : 0 ),
                'width': position.width + 2,//this._adjustSizeIfSelected( position.width,  isSelected, 2 ),
                'height': position.height + 2//this._adjustSizeIfSelected( position.height, isSelected, 2 )
            };
        }

        _adjustSizeIfSelected( value, isSelected, offset ) {
            // if selected, adjust the size, but make sure value is non-negative (e.g. for icon radius, width, etc)
            return isSelected ?
                (Math.max( 0, value + offset )) :
                value;
        }

        _radiusFromIconSizeIfValid( numCasesInPlot ) {
            let iconSize = this.getIconSize();
            if( iconSize >= 1 ) {
                // radius is half of user-specified non-zero icon size
                return iconSize / 2;
            } else {
                // else radius is based on number of cases (when iconSize undefined/zero)
                return this.calcCaseIconRadius( numCasesInPlot );
            }
        }

        setStyleCircle( numCases, radiusScaler /* optional */ ) {
            this.hideBorder = false;
            this.style = Dyn.EIconStyle.kCircle;
            Dyn.assert( this.hideBorder === this.setHideBorder() ); // check to see that setHideBorder() agrees with setting

            // set radius of circle icons based on number of cases, or user-specified size
            this.radius = this._radiusFromIconSizeIfValid( numCases );
            if( radiusScaler ) {
                // change radius by some multiple of normal size, for certain plots
                this.radius = Math.max( 1, this.radius * radiusScaler );
            }
            delete this.width;
            delete this.height;
            //console.log("setStyleCircle", "iconSize", this.iconSize, "radius", this.radius);
        }

        setStyleRectangle( numCases ) {
            this.hideBorder = true; // hide border so sorted blocks of the same color appear as one solid section of bar.
            this.style = Dyn.EIconStyle.kRect;
            Dyn.assert( this.hideBorder === this.setHideBorder() ); // check to see that setHideBorder() agrees with setting

            // set width/height from icon/radius size, as a hint for animation
            // (usually rectangles are just fit within available space within cell, so this does not affect the final plot
            //  after animation is done)
            this.radius = this._radiusFromIconSizeIfValid( numCases );
            this.width = 2 * this.radius;
            this.height = 2 * this.radius;
            //console.log("setStyleRectangle", "iconSize", this.iconSize, "radius", this.radius);
        }

        setStyleSlice( radius ) {
            this.hideBorder = true; // hide border so sorted sliced of the same color appear as one solid section of pie.
            this.style = Dyn.EIconStyle.kSlice;
            Dyn.assert( this.hideBorder === this.setHideBorder() ); // check to see that setHideBorder() agrees with setting

            // the caller determines the radius of pie slices based on available space in cells.
            this.radius = radius;
            delete this.width;
            delete this.height;
            //console.log("setStyleSlice", "iconSize", this.iconSize, "radius", this.radius);
        }

        /**
         * This style uses a custom drawing (path) for this particular dataset, e.g.
         * a person icon for a personal survey, or a hurricane icon for a hurricane location
         * dataset.  Used as an alternative to setStyleCircle()
         * @param numCases
         * @param radiusScaler
         * @returns {boolean}
         */
        setStyleCustomIcon( numCases, radiusScaler /* optional */ ) {
            let success = false;

            // set radius and other style options exactly like setStyleCircle()
            this.setStyleCircle( numCases, radiusScaler );
            // but draw as a custom icon
            this.style = Dyn.EIconStyle.kCustomIcon;
            Dyn.assert( this.hideBorder === this.setHideBorder() ); // check to see that setHideBorder() agrees with setting
            //console.log( "iconStyler style custom path");
            return success;
        }

        /**
         * This style varies the icon according to the group of icons, e.g. square
         * for Y attribute #1, circle for Y attribute #2.
         * @param numCases
         * @param radiusScaler
         * @returns {boolean}
         */
        setStyleVariableIcon( numCases, radiusScaler /* optional */ ) {
            let success = false;

            // set radius and other style options exactly like setStyleCircle()
            this.setStyleCircle( numCases, radiusScaler );
            // but draw as a custom icon
            this.style = Dyn.EIconStyle.kVariableIcon;
            Dyn.assert( this.hideBorder === this.setHideBorder() ); // check to see that setHideBorder() agrees with setting
            //console.log( "iconStyler style variable icon");
            return success;
        }

        setStyleCircleOrCustomIcon( numCases, radiusScaler /* optional */, wantVariableIconStyle ) {
            if( this.customCaseIcon && this.customCaseIcon.isEnabled ) {
                this.setStyleCustomIcon( numCases, radiusScaler );
            } else if( wantVariableIconStyle && this.allowVariableIconStyle ) {
                this.setStyleVariableIcon( numCases, radiusScaler );
            } else {
                this.setStyleCircle( numCases, radiusScaler );
            }
        }

        /** Callers can get the margin needed for this particular icon style. */
        getDesiredAxisMargin() {
            // see also other uses of extraMarginForCaseOutlines
            if( Dyn.plotUtil.isDrawOnly())
                return 0;
            switch( this.style ) {
                case Dyn.EIconStyle.kCircle:
                case Dyn.EIconStyle.kCustomIcon:
                case Dyn.EIconStyle.kVariableIcon:
                    // for dotplot/scatterplot type plots, with a numeric axis,
                    // we want to leave room for the circle icon plotted at the very edge of the plot
                    return Dyn.plotUtil.extraMarginForCaseOutlines + this.radius;
                case Dyn.EIconStyle.kRect:
                    // for bottom rectangle of histograms or value bars, e.g. numerical by categorical attribute
                    return Dyn.plotUtil.extraMarginForCaseOutlines;
                default:
                    // other plot types, don't leave any margin
                    return 0;
            }
        }

        /** show/hide the icon border */
        setHideBorder( isIncompleteAxisStyle ) {
            switch( this.style ) {
                case Dyn.EIconStyle.kRect:
                case Dyn.EIconStyle.kRectPath:
                case Dyn.EIconStyle.kSlice:
                    this.hideBorder = true; // always hide border for rect and pie slice styles
                    break;
                default:
                    this.hideBorder = Boolean( isIncompleteAxisStyle ); // else hide if incomplete plot, show otherwise
            }
            return this.hideBorder;
        }

        /**
         * Compute the SVG path string for circle.
         * Useful for animated transforms to pie slice or rectangle.
         * @param cx        - center x coordinate
         * @param cy        - center y coordinate
         * @param radius    - radius of circle
         * @returns {string}
         */
        calcCirclePath( cx, cy, radius ) {
            const minRadiusPx = 0.1; // #2063 avoid zero radius circles, which create visual artifacts, esp. in Chrome
                radius = Math.max( minRadiusPx, radius );
            var x1 = (cx).toFixed( 1 ),
                y1 = (cy - radius).toFixed( 1 ),
                x2 = (cx).toFixed( 1 ),
                y2 = (cy + radius).toFixed( 1 ),
                rotation = " 0 0,1 ", // x-axis-rotation large-arch-flag,sweepflag, 1 means clockwise
                pathString =
                    "M" + x1 + "," + y1 +	// move to top
                    " A" + radius + "," + radius + rotation + x2 + "," + y2 +  // clockwise arc to x2, y2
                    " A" + radius + "," + radius + rotation + x1 + "," + y1 +  // clockwise arc to x1, y1
                    " z";					// line back to center
            return pathString;
        }

        /**
         * Compute the SVG path string for a custom icon,
         * centered and scaled to the given coordinates.
         * @param cx        - center x coordinate
         * @param cy        - center y coordinate
         * @param radius    - radius of icon
         * @returns {string} - custom path string
         */
        calcCustomIconPath( cx, cy, radius ) {
            Dyn.assert( this.customCaseIcon );
            let bb = this.customCaseIcon.bbox,
                tx = cx - bb.cx, // path center to cx
                ty = cy - bb.cy, // path center to cy
                ts = (2 * radius) * Dyn.mathUtil.invertSafely( Math.max( bb.height, bb.width ) ), // scale to 2xRadius
                transform = "t" + tx + "," + ty + "s" + ts + "," + ts,
                pathString = Raphael.transformPath( this.customCaseIcon.pathString, transform );

            return pathString;
        }

        /** create the path of a case icon centered on (cx,cy), with given radius,
         * rotating through the available icon styles (0+)
         * @param cx
         * @param cy
         * @param radius -- desired radius or bbox width/height of icon.
         * @param iconStyle -- integer indicating which style to use
         * @returns {string}
         */
        calcVariableIconPath( cx, cy, radius, iconStyle ) {
            const availableIconStyles = 3; // to match cases 0-2 below
            // rotate through available styles
            switch( iconStyle % availableIconStyles ) {
                case 0:     // circle
                    return this.calcCirclePath( cx, cy, radius );
                case 1: {   // diamond
                    let rp = radius + 1,
                        rn = -rp;
                    return "M" + (cx + rn) + "," + cy +	// move to left-center
                        " l" + rp + "," + rn +	// line to center-top
                        " l" + rp + "," + rp +	// line to right-center
                        " l" + rn + "," + rp +	// line to center-bottom
                        "z"; // line back to left-center
                }
                case 2: {   // square
                    let size = 2 * radius;
                    return this.calcRectanglePath( cx - radius, cy - radius, size, size );
                }
                default:
                    Dyn.assert( false, "calcVariableIconPath logic error " + iconStyle );
            }
        }

        /**
         * Compute the SVG path string for a pie slice (filled arc of circle)
         * @param cx        - center x coordinate
         * @param cy        - center y coordinate
         * @param radius    - radius of circle
         * @param arcStartR    - arc start position in radians (0=noon instead of the usual 3 o'clock)
         * @param arcLengthR - arc length in radians (2 pi for a full circle)
         * @returns {string}
         */
        calcPieSlicePath( cx, cy, radius, arcStartR, arcLengthR ) {
            // Note: does not handle full circle, caller should check: if( Dyn.mathUtil.isClose( position.arcLength, Dyn.mathUtil.kTwoPi )  return calcCirclePath();
            var a1 = arcStartR + this.kFirstSliceStartAngle,
                a2 = a1 + arcLengthR,
                x1 = (cx + radius * Math.cos( a1 )).toFixed( 1 ),
                y1 = (cy + radius * Math.sin( a1 )).toFixed( 1 ),
                x2 = (cx + radius * Math.cos( a2 )).toFixed( 1 ),
                y2 = (cy + radius * Math.sin( a2 )).toFixed( 1 ),
                largeArcFlag = (arcLengthR >= Math.PI) ? 1 : 0, // note: using ">" or ">=" seems to give same results
                rotation = " 0 " + largeArcFlag + ",1 ", // x-axis-rotation large-arc-flag,sweep-flag, 1 means clockwise
                pathString =
                    "M" + cx + "," + cy +	// move to center
                    " L" + x1 + "," + y1 +	// line to x1, y1
                    " A" + radius + "," + radius + rotation + x2 + "," + y2 +  // clockwise arc to x2, y2
                    " z";					// line back to center

            return pathString;
        }

        /**
         * Return the SVG path for a pie slice, handling missing values and other special cases.
         * @param position {x,y,arcStart,arcLength,isVisible} - view position and arc angles in radians
         * @returns {string} - string for paper.path()
         */
        calcPieSliceFromPosition( position ) {
            Dyn.assertFiniteNumeric( position.x );
            Dyn.assertFiniteNumeric( position.y );
            if( position.isVisible ) {
                Dyn.assertFiniteNumeric( position.r );
                Dyn.assertFiniteNumeric( position.arcStart );
                Dyn.assertFiniteNumeric( position.arcLength );
                Dyn.assert( position.arcLength > 0, "pie with no arc should be a missing value" );
                if( Dyn.mathUtil.isClose( position.arcLength, Dyn.mathUtil.kTwoPi ) ) {
                    return this.calcCirclePath( position.x, position.y, position.r );
                }
                return this.calcPieSlicePath( position.x, position.y, position.r, position.arcStart, position.arcLength );
            } else {
                // missing cases shown in the missing case area as a circle.
                return this.calcCirclePath( position.x, position.y, position.r );
            }
        }

        /**
         * return the center point to use for labels of pie slices.
         * @param cx
         * @param cy
         * @param radius
         * @param arcStartR
         * @param arcLengthR
         * @returns {{x: *, y: *}}
         */
        calcPieSliceLabelCenterPoint( cx, cy, radius, arcStartR, arcLengthR ) {
            var bisectorAngle = this.kFirstSliceStartAngle + arcStartR + (0.5 * arcLengthR),
                labelRadius = this.kLabelRadiusFraction * radius,
                centerPoint = {
                    x: cx + labelRadius * Math.cos( bisectorAngle ),
                    y: cy + labelRadius * Math.sin( bisectorAngle )
                };
            return centerPoint;
        }

        /** calculate the pie slice angles in radians, compatible with calcPieSlicePath */
        calcPieSliceAngles( arcStartR, arcLengthR ) {
            var halfAngle = arcStartR + (0.5 * arcLengthR),
                angles = {
                    angle1: this.kFirstSliceStartAngle + arcStartR,
                    angle2: this.kFirstSliceStartAngle + arcStartR + arcLengthR,
                    angleMid: this.kFirstSliceStartAngle + halfAngle,
                    angleMidQuadrant: Dyn.mathUtil.floor( halfAngle / (Math.PI * 0.5) )
                };
            return angles;
        }

        calcAngleOfPieSlicePoint( x, y, cx, cy, angleMidQuadrant ) {
            var angle = Math.atan2( y - cy, x - cx ); // angle in +/- Math.PI radians
            return angle + Math.PI * ((angleMidQuadrant === 3) ? 4 : 2); // add  2 PI or 4 PI until in range of calcPieSliceAngles() for comparison
        }

        /**
         * Compute the SVG path string for rectangle.
         * @param x - left x coordinate
         * @param y - top y coordinate
         * @param width
         * @param height
         * @returns {string}
         */
        calcRectanglePath( x, y, width, height ) {
            var x1 = x.toFixed( 1 ),
                y1 = y.toFixed( 1 ),
                w = width.toFixed( 1 ),
                h = height.toFixed( 1 ),
                pathString =
                    "M" + x1 + "," + y1 +	// move to left-top
                    "h" + w + "v" + h + "h-" + w + // horizontal and vertical lines
                    "z";					// line back to start
            return pathString;
        }

        /**
         * Compute the SVG path string for rectangle centered on cx, cy
         */
        calcCenteredRectanglePath( cx, cy, width, height ) {
            return this.calcRectanglePath( cx - (width / 2), cy - (height / 2), width, height );
        }

        /**
         * Create an empty path at the missing value position, for path animation so values can disappear nicely.
         * @returns {string}
         */
        calcEmptyPath() {
            return ("M" + (this.missingValuePosition.x.toFixed()) + "," + (this.missingValuePosition.y.toFixed()));
        }

        /**
         * Update the view coordinates of a nice, consistent location for missing value cases,
         * so that case icons that disappear can animate to and from this point.
         */
        updateMissingValuePosition( refX, refY ) {
            this.missingValuePosition.x = refX;
            this.missingValuePosition.y = refY;
        }

        /**
         * Compute the desired point radius, which depends on the number of cases to be plotted.
         * Caller should compare to this.iconStyler.radius (actual size of case circles already created).
         */
        calcCaseIconRadius( newNumCases ) {
            // Search for a point size in [min-max] range, point size decreases with every power of kCaseIconRadiusBase
            // This step function avoids excessive plot resizing with every change in number of cases.
            var tRadius = this.kCaseIconRadiusMax,
                tMinSize = this.kCaseIconRadiusMin,
                tPower = this.kCaseIconRadiusBase;

            // for loop is fast equivalent to radius = max( minSize, maxSize - floor( log( Base, max( dataLength, 1 )))
            for( var i = tPower; i <= newNumCases; i = i * tPower ) {
                --tRadius;
                if( tRadius <= tMinSize ) break;
            }
            Dyn.assert( tRadius > 0 && Math.round( tRadius ) === tRadius ); // must be positive integer
            //if ( tRadius !== this.radius )
            //	console.log( "calcCaseIconRadius() r=" + tRadius + " for " + newNumCases + " cases." );

            return tRadius;
        }

        /**
         * Remove an old case icon (Rafael element), and save enough positional data
         * so that a new case icon of a different type can be created in its place.
         * @param oldElement - Rafael element (circle, rect, path), or null
         * @param oldIconStyle - EIconStyle
         * @param ioPosition - positional data to modify
         */
        extractOldCasePositionData( oldElement, oldIconStyle, nextIconStyle, ioPosition ) {
            var pathArray,
                old = {};

            // copy case data for use in re-creating an icon at the old position
            old.caseIndex = ioPosition.caseIndex;
            old.xAttrIndex = ioPosition.xAttrIndex;
            old.yAttrIndex = ioPosition.yAttrIndex;
            old.isVisible = ioPosition.isVisible;

            // get defaults for old position, if icon was removed (null), or unknown format
            old.x = ioPosition.last ? ioPosition.last.x : this.missingValuePosition.x;
            old.y = ioPosition.last ? ioPosition.last.y : this.missingValuePosition.y;
            switch( oldIconStyle ) {
                case Dyn.EIconStyle.kCircle:
                case Dyn.EIconStyle.kCirclePath:
                case Dyn.EIconStyle.kSlice:
                case Dyn.EIconStyle.kCustomIcon:
                case Dyn.EIconStyle.kVariableIcon:
                    old.r = 1; // or this.radius?
                    break;
                case Dyn.EIconStyle.kRect:
                case Dyn.EIconStyle.kRectPath:
                    old.width = 1; // or this.getWidthOrHeight(true)?
                    old.height = 1; // or this.getWidthOrHeight(false)?
                    break;
            }

            // now extract old position from icon if possible, and create data needed by nextIconStyle
            switch( oldIconStyle ) {
                case Dyn.EIconStyle.kCircle:
                    // Guarantee that {x,y,r} is available to animate from old circle position
                    // or {path} if doing a path-to-path animation.
                    if( oldElement ) {
                        old.x = oldElement.attr( "cx" );
                        old.y = oldElement.attr( "cy" );
                        old.r = oldElement.attr( "r" );
                    }  // else use old-position defaults
                    Dyn.assert( isFinite( old.r ) );

                    switch( nextIconStyle ) {
                        case Dyn.EIconStyle.kCircle:
                        case Dyn.EIconStyle.kCirclePath:
                        case Dyn.EIconStyle.kCustomIcon:
                        case Dyn.EIconStyle.kVariableIcon:
                            break;
                        case Dyn.EIconStyle.kRect:
                        case Dyn.EIconStyle.kRectPath:
                            // offset from circle center-center to left-top
                            old.x = old.x - old.r;
                            old.y = old.y - old.r;
                            old.width = old.height = 2 * old.r;
                            break;
                        case Dyn.EIconStyle.kSlice:
                            old.path = this.calcCirclePath( old.x, old.y, old.r );
                            break;
                    }
                    break;
                case Dyn.EIconStyle.kCirclePath:
                    // Guarantee that {x,y,r} is available to animate from old circle position,
                    // or {path} if doing a path-to-path animation.
                    old.path = pathArray = (oldElement ? oldElement.attr( "path" ) : ""); // may be string or Array
                    Dyn.assert( isFinite( old.r ) );

                    if( pathArray.length === 4 && pathArray[1][0] === "A" ) {
                        // SVG path commands "M", "A", "A", "z" created by calcCirclePath()
                        old.r = 0.5 * Math.abs( pathArray[1][7] - pathArray[2][7] ); // half of dist from y1 to y2
                        old.x = pathArray[0][1];
                        old.y = pathArray[0][2] + old.r;
                    } else if( pathArray.length >= 4 && pathArray[1][0] === "C" ) {
                        // SVG path commands 'M', 'C', 'C', 'C', 'C'... created by intermediate animation steps
                        //Dyn.assert( pathArray[1][0]==="C", "unknown path");
                        old.x = pathArray[0][1];
                        old.y = pathArray[0][2];
                        old.r = this.radius;
                    } // else use old-position defaults

                    switch( nextIconStyle ) {
                        case Dyn.EIconStyle.kCircle:
                        case Dyn.EIconStyle.kCirclePath:
                        case Dyn.EIconStyle.kCustomIcon:
                        case Dyn.EIconStyle.kVariableIcon:
                            break;
                        case Dyn.EIconStyle.kRect:
                        case Dyn.EIconStyle.kRectPath:
                            // offset from circle center-center to left-top
                            old.x = old.x - old.r;
                            old.y = old.y - old.r;
                            old.width = old.height = 2 * old.r;
                            break;
                        case Dyn.EIconStyle.kSlice:
                            if( !old.path ) {
                                old.path = this.calcCirclePath( old.x, old.y, old.r );
                            }
                            break;
                    }
                    break;
                case Dyn.EIconStyle.kRect:
                    // Guarantee that {x,y,width,height} is available to animate from old rectangle position,
                    // or {path} if doing a path-to-path animation.
                    if( oldElement ) {
                        old.x = oldElement.attr( "x" );
                        old.y = oldElement.attr( "y" );
                        old.width = oldElement.attr( "width" );
                        old.height = oldElement.attr( "height" );
                    } // else use old-position defaults
                    Dyn.assert( isFinite( old.width ) && isFinite( old.height ) );

                    switch( nextIconStyle ) {
                        case Dyn.EIconStyle.kCircle:
                        case Dyn.EIconStyle.kCustomIcon:
                        case Dyn.EIconStyle.kVariableIcon:
                            // offset from left-top to center-center
                            old.r = 0.5 * Math.min( old.width, old.height );
                            old.x = old.x + old.width / 2;
                            old.y = old.y + old.height / 2;
                            break;
                        case Dyn.EIconStyle.kCirclePath:
                        case Dyn.EIconStyle.kRect:
                        case Dyn.EIconStyle.kRectPath:
                            break;
                        case Dyn.EIconStyle.kSlice:
                            old.path = this.calcRectanglePath( old.x, old.y, old.width, old.height );
                            break;
                    }
                    break;
                case Dyn.EIconStyle.kRectPath:
                    // Guarantee that {x,y,width,height} is available to animate to rectangle position,
                    // or {x,y,r} if animate to circle,
                    // or {path} if doing a path-to-path animation.
                    old.path = pathArray = (oldElement ? oldElement.attr( "path" ) : ""); // may be string or Array

                    if( pathArray.length === 5 && pathArray[1][0] === "H" ) {
                        // SVG path commands "M", "h", "v", "h", "z" elements created by calcRectanglePath()
                        old.x = pathArray[0][1];
                        old.y = pathArray[0][2];
                        old.width = pathArray[1][1] - old.x; // converted 'h' to absolute 'H'
                        old.height = pathArray[2][1] - old.y; // converted 'v' to absolue 'V'
                    } else if( pathArray.length >= 5 /*&& typeof value === "object"*/ ) {
                        // SVG path commands 'M', 'C', 'C', 'C', 'C'... created by intermediate animation steps
                        Dyn.assert( [ 'C', 'L' ].includes( pathArray[1][0] ), "unknown path" );
                        old.x = pathArray[0][1];
                        old.y = pathArray[0][2];
                        old.width = this.getWidthOrHeight( true );
                        old.height = this.getWidthOrHeight( false );
                    } // else use old-position defaults
                    Dyn.assert( isFinite( old.width ) && isFinite( old.height ) );

                    switch( nextIconStyle ) {
                        case Dyn.EIconStyle.kCircle:
                        case Dyn.EIconStyle.kCirclePath:
                        case Dyn.EIconStyle.kCustomIcon:
                        case Dyn.EIconStyle.kVariableIcon:
                            // offset from left-top to center-center
                            old.r = 0.5 * Math.min( old.width, old.height );
                            old.x = old.x + old.width / 2;
                            old.y = old.x + old.width / 2;
                            break;
                        case Dyn.EIconStyle.kRect:
                        case Dyn.EIconStyle.kRectPath:
                            break;
                        case Dyn.EIconStyle.kSlice:
                            if( !old.path ) {
                                old.path = this.calcRectanglePath( old.x, old.y, old.width, old.height );
                            }
                            break;
                    }
                    break;
                case Dyn.EIconStyle.kSlice:
                case Dyn.EIconStyle.kCustomIcon:
                case Dyn.EIconStyle.kVariableIcon:
                    // get arbitrary path to animate from
                    old.path = pathArray = (oldElement ? oldElement.attr( "path" ) : ""); // may be string or Array

                    // estimate the position and size of the path for animating to circles or rectangles
                    if( pathArray.length === 4 && pathArray[1][0] === "L" ) {
                        // SVG path commands "M", "L", "A", "z" created by calcPieSlicePath()
                        old.x = 0.5 * (pathArray[1][1] + pathArray[2][6]); // midpoint of arc endpoints
                        old.y = 0.5 * (pathArray[1][2] + pathArray[2][7]); // midpoint of arc endpoints
                        old.r = pathArray[2][1]; // radius of pie (warning: this is a larger value than circle radius)
                    } else if( pathArray.length === 4 && pathArray[1][0] === "A" ) {
                        // SVG path commands "M", "A", "A", "z" created by calcCirclePath()
                        old.r = 0.5 * Math.abs( pathArray[1][7] - pathArray[2][7] ); // half of dist from y1 to y2
                        old.x = pathArray[0][1];
                        old.y = pathArray[0][2] + old.r;
                    } else if( pathArray.length >= 4 && (typeof pathArray === "object") ) {
                        if( pathArray[1][0] === "C" ) {
                            // SVG path commands 'M', 'C', 'C', 'C', 'C'... created by intermediate animation steps
                            old.x = pathArray[0][1];
                            old.y = pathArray[0][2];
                            old.r = this.radius;
                        } else { // unknown path
                            let bbox = oldElement.getBBox();
                            old.x = bbox.cx;
                            old.y = bbox.cy;
                            old.r = bbox.cx - bbox.x;
                        }
                    }
                    Dyn.assert( isFinite( old.r ) );

                    switch( nextIconStyle ) {
                        case Dyn.EIconStyle.kCircle:
                        case Dyn.EIconStyle.kCirclePath:
                            break;
                        case Dyn.EIconStyle.kRect:
                        case Dyn.EIconStyle.kRectPath:
                            // offset from circle center-center to left-top
                            old.x = old.x - old.r;
                            old.y = old.y - old.r;
                            old.width = old.x + old.r;
                            old.height = old.y + old.r;
                            break;
                        case Dyn.EIconStyle.kSlice:
                        case Dyn.EIconStyle.kCustomIcon:
                        case Dyn.EIconStyle.kVariableIcon:
                            if( !old.path ) {
                                old.path = this.calcCirclePath( old.x, old.y, old.r );
                            }
                            break;
                    }
                    break;
            }

            // save old positional data, to be cleared by removeOldCasePositionData()
            ioPosition.old = old;
        }

        /**
         * Remove the positional data not used by the new icon style
         * @param ioPosition
         * @param oldIconStyle
         */
        removeOldCasePositionData( ioPosition, oldIconStyle ) {
            delete ioPosition.old;
            delete ioPosition.new;

            if( oldIconStyle !== this.style ) {
                switch( oldIconStyle ) {
                    case Dyn.EIconStyle.kCircle:
                    case Dyn.EIconStyle.kCustomIcon:
                    case Dyn.EIconStyle.kVariableIcon:
                        // keep x,y,r
                        break;
                    case Dyn.EIconStyle.kRect:
                        // keep x,y
                        delete ioPosition.width;
                        delete ioPosition.height;
                        break;
                    case Dyn.EIconStyle.kSlice:
                        // keep x,y,r
                        delete ioPosition.arcStart;
                        delete ioPosition.arcLength;
                        break;
                }
            }
        }

        /**
         * Calculate the new case position data used to animate the case icon to, returned as position.new
         * The case icon may have slight variants from the plot calculations for proper alignment of visual elements.
         * @param position - target case position data as calculated by the plot
         * @param currentIconStyle - the current icon style in use in the plot view.
         * @param nextIconStyle - the target or intermediate icon style to use next, see getNextIconStyleForAnimations()
         * This method calculates the attribute values of the new position of the case icon, following
         * nextIconStyle, e.g. {path} for paper.path, {x,y,height,width} for paper.rect, {r,cx,cy} for paper.circle(), etc.
         * See also extractOldCasePositionData(), createInitialCasePositionData(), which are coordinated with this method
         * to create animations of the same type, e.g. circle path to pie slice path, to animation from dot plot to pie chart.
         */
        calcNewCasePositionData( position, fillColor, strokeColor, isSelected, oldIconStyle, nextIconStyle ) {
            var targetIconStyle = this.style,
                newPos = {},
                radius;

            switch( targetIconStyle ) {
                case Dyn.EIconStyle.kCircle:
                    Dyn.assertValidCircle( position );
                    radius = this.getRadius( position, isSelected );
                    if( nextIconStyle === Dyn.EIconStyle.kCirclePath ) {
                        newPos.path = this.calcCirclePath( position.x, position.y, radius );
                    } else {
                        newPos = {
                            'cx': position.x,
                            'cy': position.y,
                            'r': radius
                        };
                    }
                    break;
                case Dyn.EIconStyle.kRect:
                    Dyn.assertValidRect( position );
                    if( nextIconStyle === Dyn.EIconStyle.kRectPath ) {
                        newPos.path = this.calcRectanglePath( position.x, position.y, position.width, position.height );
                    } else {
                        newPos = {
                            'x': position.x,
                            'y': position.y,
                            'width': position.width,
                            'height': position.height
                        };
                    }
                    break;
                case Dyn.EIconStyle.kSlice:
                    newPos.path = this.calcPieSliceFromPosition( position );
                    break;
                case Dyn.EIconStyle.kCustomIcon:
                    Dyn.assertValidCircle( position );
                    radius = this.getRadius( position, isSelected );
                    newPos.path = this.calcCustomIconPath( position.x, position.y, radius );
                    break;
                case Dyn.EIconStyle.kVariableIcon:
                    Dyn.assertValidCircle( position );
                    radius = this.getRadius( position, isSelected );
                    newPos.path = this.calcVariableIconPath( position.x, position.y, radius, this.getVariableIconIndex( position ) );
                    break;
            }
            newPos.fill = fillColor.colorString;
            newPos.stroke = strokeColor;

            // save new positional data, to be cleared by removeOldCasePositionData()
            position.new = newPos;
        }

        /**
         * Save the last know position in case the icon is going to be deleted,
         * to be used as the default "old" position by extractOldCasePositionData() when the icon becomes visible again.
         * @param position
         */
        saveLastPostionDataForInvisibleIcon( position ) {
            var last = { x: position.x, y: position.y };
            Dyn.assert( last.x, last.y );
            position.last = last;
        }

        /**
         * Get the initial positional data needed to create a new case icon,
         * These values are a combination of an initial starting point, plus
         * the final values to be set with animation later.
         * @param ioInitialPosition - initial position to set (may have case invarient parameters)
         * @param finalPosition - final position icon will be animated to
         * @param isInitialLoadOfPlot
         * @param noAnimation
         */
        createInitialCasePositionData( ioInitialPosition, finalPosition, isInitialLoadOfPlot, noAnimation ) {
            // create case-invariant parameters first
            if( typeof ioInitialPosition.r === "undefined" ) {
                // start circles, slices with tiny radius
                ioInitialPosition.r = ((isInitialLoadOfPlot && noAnimation) ? this.radius : 1);
                // start rectangles as tiny square
                ioInitialPosition.width = ioInitialPosition.height = 1;
                // start slices as a full circle
                ioInitialPosition.arcStart = this.kFirstSliceStartAngle;
                ioInitialPosition.arcLength = Dyn.mathUtil.kTwoPi;
            }
            // case-variant parameters
            ioInitialPosition.x = this.missingValuePosition.x;	// all case icons appear here
            ioInitialPosition.y = this.missingValuePosition.y;
            ioInitialPosition.caseIndex = finalPosition.caseIndex;
            ioInitialPosition.xAttrIndex = finalPosition.xAttrIndex;
            ioInitialPosition.yAttrIndex = finalPosition.yAttrIndex;
            ioInitialPosition.isVisible = finalPosition.isVisible;
        }

        /**
         * If needing to animate from the current icon style to the target icon style,
         * What intermediate (next) icon style is needed, if any?
         * @param currentIconStyle
         */
        getNextStyleForAnimations( currentIconStyle ) {
            var targetIconStyle = this.style,
                nextIconStyle = this.style; // default is to keep style as set by plot elements.

            switch( currentIconStyle ) {
                case Dyn.EIconStyle.kCircle:
                case Dyn.EIconStyle.kCirclePath:
                    // use intermediate icon type kRectPath for smooth animations from circle to rectangle
                    if( targetIconStyle === Dyn.EIconStyle.kRect ) {
                        nextIconStyle = Dyn.EIconStyle.kRectPath;
                    }
                    break;
                case Dyn.EIconStyle.kRect:
                case Dyn.EIconStyle.kRectPath:
                    // use intermediate icon type kCirclePath for smooth animations from rectangle to circle
                    if( targetIconStyle === Dyn.EIconStyle.kCircle ) {
                        nextIconStyle = Dyn.EIconStyle.kCirclePath;
                    }
                    break;
                case Dyn.EIconStyle.kSlice:
                case Dyn.EIconStyle.kCustomIcon:
                case Dyn.EIconStyle.kVariableIcon:
                    // use intermediate icon type kCirclePath/kRectPath for smooth animations from pie slice/custom icon to circle/rect.
                    if( targetIconStyle === Dyn.EIconStyle.kCircle ) {
                        nextIconStyle = Dyn.EIconStyle.kCirclePath;
                    }
                    if( targetIconStyle === Dyn.EIconStyle.kRect ) {
                        nextIconStyle = Dyn.EIconStyle.kRectPath;
                    }
                    break;
            }
            return nextIconStyle;
        }

        getCurrentIconStyle() {
            return this.style;
        }

        getVariableIconIndex( position ) {
            // our plots have either multiple x attributes or multiple y attributes, not both.
            // we want icon 0 for y attribute 0, icon 1 for y attribute 0, etc.
            // return index 0+
            return Math.max( position.xAttrIndex, position.yAttrIndex );
        }


    } // class IconStyler
    Dyn.IconStyler = IconStyler;
} // wrapDynIconStyler
